import { type FC, type ReactNode, useMemo } from "react";
import { View, type DimensionValue, type StyleProp, type ViewStyle } from "react-native";
import { StyleSheet } from "react-native-unistyles";
import "../../theme";
import { TableContextProvider, useTableHorizontalScroll, useTableLayout, type TableSize } from "./TableContext";

export interface TableProps {
  /** `TableHead`, `TableBody` and `TableFooter` sections. */
  readonly children: ReactNode;
  /** Row density for every cell in the table. Defaults to `"medium"`. */
  readonly size?: TableSize | undefined;
  /**
   * Width the columns need. Below it, a `TableContainer` with
   * `horizontalScroll` scrolls sideways instead of squeezing the columns.
   * Ignored in the stacked layout, where there are no columns.
   */
  readonly minWidth?: DimensionValue | undefined;
  readonly style?: StyleProp<ViewStyle> | undefined;
  readonly testID?: string | undefined;
  readonly accessibilityLabel?: string | undefined;
}

/**
 * Root of a data table, modelled on MUI's `Table`. Built from views rather than
 * HTML table elements so it renders the same on native and web; columns line up
 * because each column's `TableCell`s share the same `width` or `flex`.
 *
 * Place it in a `TableContainer`. Inside a container with a bounded height the
 * table fills it, so a virtualised list in `TableBody` scrolls under a fixed header.
 */
const Table: FC<TableProps> = ({ children, size = "medium", minWidth, style, testID, accessibilityLabel }) => {
  const context = useMemo(() => ({ size }), [size]);
  const horizontalScroll = useTableHorizontalScroll();
  const stacked = useTableLayout() === "stacked";

  return (
    <TableContextProvider value={context}>
      <View
        role="table"
        aria-label={accessibilityLabel}
        testID={testID}
        style={[
          styles.table,
          minWidth !== undefined &&
            !stacked &&
            // In a horizontal scroll view the table would otherwise size to its
            // widest content (untruncated `noWrap` text). Start from `minWidth`
            // and let `flexGrow` widen it to fill the container.
            (horizontalScroll ? { width: minWidth } : { minWidth }),
          style,
        ]}
      >
        {children}
      </View>
    </TableContextProvider>
  );
};

Table.displayName = "Table";

export { Table };

const styles = StyleSheet.create({
  table: {
    // Grow into a bounded container (and across a horizontal scroll view), but
    // keep content height otherwise: `flex: 1` would collapse an unbounded table.
    flexGrow: 1,
    flexShrink: 1,
    minHeight: 0,
    // Every row draws a bottom divider. Pulling the table up by one pixel tucks
    // the last row's divider under the container's clipped edge instead of
    // doubling the border. Relies on the `TableContainer` clipping.
    marginBottom: -1,
  },
});
